import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sort, setSort] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCat = categories.find(c => c.slug === slug) || categories[0];
  const isAll = currentCat.slug === 'all';

  const filtered = useMemo(() => {
    let result = isAll ? [...comics] : comics.filter(c => c.category === currentCat.slug);
    if (sort === 'popular') result.sort((a, b) => b.views - a.views);
    else if (sort === 'rating') result.sort((a, b) => b.rating - a.rating);
    else result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    return result;
  }, [currentCat.slug, isAll, sort]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCat.name },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-2">
              {currentCat.name}韩漫
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filtered.length} 部作品
            </p>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-wrap">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentCat.slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-background-50 rounded-lg p-1 border border-background-200">
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '人气' },
                { key: 'rating', label: '评分' },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSort(opt.key as typeof sort)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sort === opt.key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
            {filtered.map(comic => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画无删减`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="inline-block bg-accent-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {comic.rating}
                    </span>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span
                      className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        comic.status === '连载中'
                          ? 'bg-primary-500 text-white'
                          : 'bg-secondary-500 text-white'
                      }`}
                    >
                      {comic.status}
                    </span>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto"></i>
              <p className="text-foreground-500 mt-4">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}